import { css } from "@emotion/react";
import { type DoctorExpanded, type Slot } from "@hermes/api";
import { Message, Temporal } from "@hermes/intl";
import {
  colors,
  createStaticStyles,
  cssFns,
  useScreenDetector,
  useStaticStyles,
} from "@hermes/ui";
import { useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";
import Skeleton from "react-loading-skeleton";

import {
  selectMainDoctorService,
  sortDoctorServiceByFeedback,
  sortDoctorServicesByType,
} from "#internal/shared/utils";

import type { AppointmentModalProps } from "../appointment-modal";
import { AppointmentSlots } from "../appointment-slots";
import { useCity } from "../city";
import {
  DoctorClinicInfo,
  DoctorClinicMapInfo,
  DoctorClinicMetroInfo,
} from "./clinic-info";
import { DoctorDesktopBranchSelect } from "./desktop-branch-select";
import { MobileBranchSelect } from "./mobile-branch-select";
import { DoctorProfileInfoV2 } from "./profile-info";
import { DoctorProfileReviewV2 } from "./profile-review";
import { DoctorServicePrice } from "./service-price";

export type DoctorCardOptions = {
  // Tie the logic by those services
  targetServicesIds?: number[];
  premium?: boolean;
  integrated?: boolean;
  request_meta?: AppointmentModalProps["request_meta"];
  isBordered?: boolean;
  heading?: "h1" | "h2" | "h3";
  noRedirectToClinic?: boolean;
  displayAllReviews?: boolean;
  displayCityArea?: boolean;
  displaySpecialties?: boolean;
  displayMinPrice?: boolean;
  clinicId?: number;
};

const styles = {
  container: {
    border: {
      common: cssFns.border({
        width: 1,
        style: "solid",
        color: colors.principal.grayline,
      }),
      premium: cssFns.border({
        width: 1,
        style: "solid",
        color: colors.principal.ebblue,
      }),
    },
    hide: createStaticStyles({
      common: { display: "none" },
    }),
    common: createStaticStyles({
      common: {
        display: "grid",
        borderRadius: 12,
        backgroundColor: colors.principal.white,
      },
      mobile: { gridTemplateColumns: "1fr" },
      desktop: { gridTemplateColumns: "1fr auto 340px" },
    }),
  },
  mainInfo: {
    container: createStaticStyles({
      common: {
        display: "grid",
        gridTemplateColumns: "auto 1fr",
      },
      mobile: [
        cssFns.padding(10),
        {
          gridTemplateRows: "minmax(122px, auto) repeat(4, auto)",
          columnGap: 12,
        },
      ],
      desktop: [
        cssFns.padding(26, 20),
        {
          gridTemplateRows: "auto auto 1fr",
          columnGap: 30,
        },
      ],
    }),
    review: createStaticStyles({
      common: cssFns.gridColumn(1, 2),
      mobile: cssFns.gridRow(1, 2),
      desktop: cssFns.gridRow(1, 4),
    }),
    profileInfo: createStaticStyles.clean([
      cssFns.gridColumn(2, 3),
      cssFns.gridRow(1, 2),
      { marginBlockEnd: 16 },
    ]),
  },
  mobileCutoff: createStaticStyles.clean([
    cssFns.borderBlockEnd({
      color: colors.principal.grayline,
      width: 1,
      style: "solid",
    }),
    cssFns.gridRow(2, 3),
    cssFns.gridColumn(1, 3),
    { marginBlock: 16 },
  ]),
  service: {
    container: createStaticStyles({
      mobile: [
        cssFns.gridColumn(1, 3),
        cssFns.gridRow(4, 5),
        { marginBlockEnd: 6 },
      ],
      desktop: [
        cssFns.gridColumn(2, 3),
        cssFns.gridRow(2, 3),
        { marginBlockEnd: 24 },
      ],
    }),
    label: createStaticStyles.clean([
      cssFns.typo({ weight: "regular", level: "body-1" }),
      { color: colors.principal.gs4, marginBlockEnd: 4 },
    ]),
  },
  clinic: {
    container: createStaticStyles({
      common: cssFns.gridRow(3, 4),
      mobile: [cssFns.gridColumn(1, 3), { marginBlockEnd: 16 }],
      desktop: cssFns.gridColumn(2, 3),
    }),
    desktopSelector: createStaticStyles.clean({
      paddingBlockStart: 26,
      marginInlineEnd: -1,
      zIndex: 1,
    }),
  },
  slots: createStaticStyles({
    common: cssFns.borderInlineStart({
      color: colors.principal.grayline,
      width: 1,
      style: "solid",
    }),
    mobile: [
      cssFns.padding(16, 10),
      {
        paddingBlockStart: 0,
        alignSelf: "flex-end",
      },
    ],
    desktop: cssFns.padding(20, 16),
  }),
};

export const DoctorCardV2 = ({
  doctor,
  slots,
  onInView,
  options,
}: {
  doctor: DoctorExpanded & { has_slots?: boolean };
  slots: {
    initial?: Slot[];
    defaultStartDate?: Temporal.PlainDate;
    isFetching?: boolean;
  };
  onInView?: () => void;
  options?: DoctorCardOptions;
}) => {
  const { staticStyles } = useStaticStyles();
  const { isMobile } = useScreenDetector();
  const { resolveCityById } = useCity();

  const view = useInView({
    triggerOnce: true,
    threshold: 0,
    onChange(inView) {
      if (inView) {
        onInView?.();
      }
    },
  });

  const { targetServices, branches, branchesMap } = useMemo(() => {
    // Services filtered by targetServicesIds and optionally premium availability
    const targetServices = doctor.services
      .filter((dService) => {
        return (
          (options?.targetServicesIds
            ? options.targetServicesIds.includes(dService.service.id)
            : true) &&
          (typeof options?.premium !== "undefined"
            ? dService.is_premium === options.premium
            : true)
        );
      })
      .sort(sortDoctorServiceByFeedback);
    const branchesMap = doctor.clinic_branches
      .filter((branch) =>
        options?.clinicId ? branch.clinic === options.clinicId : true,
      )
      .toMap((branch) => branch.id);
    const servicesByBranches = targetServices.groupBy(
      (service) => service.clinic_branch,
    );

    // Get all branches that have at least one of the targeted services
    const branches = doctor.is_active
      ? doctor.clinic_branches.filter((branch) => {
          return (
            servicesByBranches[branch.id] &&
            (options?.clinicId ? branch.clinic === options.clinicId : true)
          );
        })
      : [];

    return {
      targetServices,
      branches,
      branchesMap,
    };
  }, [
    options?.targetServicesIds,
    options?.premium,
    doctor.services,
    doctor.clinic_branches,
    options?.clinicId,
  ]);

  const [branchId, setBranchId] = useState<number | undefined>(branches[0]?.id);
  const selectedBranch = branchId ? branchesMap.get(branchId) : undefined;
  const selectedCity = resolveCityById(selectedBranch?.city);
  const selectedCityArea = options?.displayCityArea
    ? selectedBranch?.city_area
    : undefined;

  // If every targeted service is a procedure, try to display initial appointments
  // otherwise, just display targeted services
  const {
    branchServices,
    displayServices,
    allBranchServices,
    minPriceService,
  } = useMemo(() => {
    const branchServices = sortDoctorServicesByType({
      services: targetServices,
      selectedBranchId: selectedBranch?.id,
      includeProcedures: true,
    });
    const specialties = sortDoctorServicesByType({
      services: doctor.services,
    });
    const allBranchServices = sortDoctorServicesByType({
      services: doctor.services,
      includeProcedures: true,
    });

    const byMinPrices = options?.displayMinPrice
      ? branchServices.sort((a, b) => {
          if (a.price && b.price) {
            return a.price - b.price;
          }

          return 0;
        })
      : undefined;

    return {
      branchServices: branchServices,
      // Want to display only doctor's specialties if possible
      // Fallback to services available by the branch
      displayServices: (options?.displaySpecialties && specialties.length
        ? specialties
        : branchServices
      )
        .map((dService) => dService.service.name)
        .unique(),
      allBranchServices,
      minPriceService:
        byMinPrices?.find(
          (dService) =>
            dService.service.type === "initial-appointment" &&
            dService.total !== null,
        ) ??
        byMinPrices?.find((dService) => dService.total !== null) ??
        byMinPrices?.[0],
    };
  }, [
    targetServices,
    selectedBranch?.id,
    options?.displaySpecialties,
    options?.displayMinPrice,
  ]);

  const selectedService = useMemo(
    () =>
      selectedBranch ? selectMainDoctorService(branchServices) : undefined,
    [selectedBranch?.id, branchServices],
  );

  const station = selectedBranch?.closest_stations?.[0];

  const { branchSlots } = useMemo(() => {
    return {
      branchSlots: slots.initial?.filter((slot) => {
        return slot.clinic_branch === selectedService?.clinic_branch;
      }),
    };
  }, [slots.initial, selectedService]);

  return (
    <div
      ref={view.ref}
      css={[
        options?.isBordered && isMobile
          ? styles.container.border.common
          : options?.premium
            ? styles.container.border.premium
            : undefined,
        staticStyles(styles.container.common),
      ]}
    >
      <div css={staticStyles(styles.mainInfo.container)}>
        <div css={staticStyles(styles.mainInfo.review)}>
          <DoctorProfileReviewV2
            doctor={doctor}
            doctorService={selectedService}
            displayAllReviews={options?.displayAllReviews}
          />
        </div>
        <DoctorProfileInfoV2
          styles={styles.mainInfo.profileInfo}
          doctor={doctor}
          heading={options?.heading}
          premium={options?.premium}
          displayAllReviews={options?.displayAllReviews}
          displaySpecialties={options?.displaySpecialties}
          doctorService={selectedService}
          availableServices={displayServices}
        />
        {isMobile && <div css={styles.mobileCutoff} />}
        {selectedService && doctor.has_slots && (
          <div css={staticStyles(styles.service.container)}>
            <div css={styles.service.label}>
              {selectedService.service.type === "initial-appointment" ||
              minPriceService?.service.type === "initial-appointment" ? (
                <Message
                  id="eb08a7ce-3633-4934-a3a7-79f49d1042c2"
                  default="Прием в клинике"
                />
              ) : (
                (minPriceService?.service.name ?? selectedService.service.name)
              )}
            </div>
            <DoctorServicePrice
              styles={css({
                width: isMobile ? undefined : "fit-content",
              })}
              {...(minPriceService && branchServices.length > 1
                ? {
                    total: minPriceService.total,
                    displayAsFrom: true,
                  }
                : {
                    discount: selectedService.discount,
                    price: selectedService.price,
                    total: selectedService.total,
                    displayAsFrom: selectedService.service.type === "procedure",
                    insurances: selectedService.insurances,
                    info: selectedService.price_conditions,
                  })}
            />
          </div>
        )}
        {doctor.has_slots && (
          <div
            css={[
              staticStyles(styles.clinic.container),
              !selectedBranch && staticStyles(styles.container.hide),
            ]}
            data-doctor
          >
            <div css={{ marginBlockEnd: 6 }}>
              {selectedBranch &&
                (isMobile && branches.length > 1 ? (
                  <MobileBranchSelect
                    branches={branches}
                    selected={selectedBranch}
                    onSelectionChange={setBranchId}
                    displayCityArea={options?.displayCityArea}
                  />
                ) : (
                  <DoctorClinicInfo
                    name={selectedBranch.name}
                    address={selectedBranch.address}
                    slug={selectedBranch.clinic_slug}
                    city={selectedCity}
                    cityArea={selectedCityArea}
                    noRedirectToClinic={options?.noRedirectToClinic}
                  />
                ))}
            </div>
            {station && station.itineraries[0] && (
              <div css={{ marginBlockEnd: 6 }}>
                <DoctorClinicMetroInfo
                  name={station.name}
                  distance={station.distance}
                  icon_url={station.itineraries[0].icon_abs_url}
                  icon_alt={
                    station.itineraries[0].short_name ||
                    station.itineraries[0].name
                  }
                />
              </div>
            )}
            {selectedBranch && (
              <DoctorClinicMapInfo
                lat={selectedBranch.location.lat}
                lng={selectedBranch.location.lng}
                distance={selectedBranch.distance}
              />
            )}
          </div>
        )}
      </div>
      {!isMobile && (
        <div css={styles.clinic.desktopSelector}>
          {
            <DoctorDesktopBranchSelect
              branches={branches}
              selected={selectedBranch}
              onSelectionChange={setBranchId}
            />
          }
        </div>
      )}
      <div css={staticStyles(styles.slots)}>
        {!slots.isFetching ? (
          <AppointmentSlots
            doctor={doctor}
            selectedBranch={selectedBranch}
            displayServices={allBranchServices.map((dService) => ({
              service: dService.service.id,
              service_name: dService.service.name,
              service_type: dService.service.type,
              age_to: dService.age_to,
            }))}
            city={selectedCity}
            cityArea={selectedCityArea}
            initialSlots={branchSlots}
            defaultStartDate={slots.defaultStartDate}
            request_meta={options?.request_meta}
            displayAllReviews={options?.displayAllReviews}
            selectedDoctorService={selectedService}
          />
        ) : (
          <Skeleton height={isMobile ? 59 : 120} />
        )}
      </div>
    </div>
  );
};
